/* eslint-disable no-underscore-dangle */
/* eslint-disable no-unused-vars */
import React from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '@stitch-fix/i18n';
import { Carousel, Box, ModuleHeader } from '@stitch-fix/mode-react';
import { useViewEvent } from '@stitch-fix/event-reporter';
import { InView } from 'react-intersection-observer';
import ProductCard from '@stitch-fix/mode-react/ProductCard';
import { FeedbackThumbs } from '@stitch-fix/product-card';
import ErrorBoundary from '../shared/ErrorBoundary';
import InlineErrorBoundary from '../shared/InlineErrorBoundary';
import {
  formatShopProductDetails,
  formatShopProductImage,
} from '../../helpers/productHelper';
import './styles.module.scss';
import EventedEndCard from '../shared/EventedEndCard';
import { useProductDetailsPageContext } from '../../../../ProductDetailsPage/ProductDetailsPageContext/ProductDetailsPageContext';

/**
 * Collection Carousel displays a generic list of clothing items
 * which may potentially contain products or outfits
 */
const CollectionCarousel = ({
  collectionTrackingId,
  treatment,
  title,
  items,
  onCarouselVisible,
  onItemVisible,
  onItemClick,
  onClickEndCard,
  onClickViewAll,
  onEndCardVisible,
  brandCategoryLink,
  shopItemPath,
}) => {
  const [state] = useProductDetailsPageContext();

  const showFeedbackThumbs =
    state.clientFeatureFlags?.['client.feedback_thumbs_enabled'] || false;

  const { ref, EventProvider, reportSelectEvent } = useViewEvent({
    schema: 'category_view',
    category_id: collectionTrackingId,
    name: 'carousel',
  });

  const { locale } = useI18n();

  if (!items.length) {
    return null;
  }

  let brand;
  let name;
  let price;
  let discountLabel;
  let fullPrice;

  const renderItem = (item, index) => {
    ({ brand, name, price, discountLabel, fullPrice } =
      formatShopProductDetails({ locale, node: item }));

    const showMarkdown = state.gated && !!item?.variantBySkuId?.priceMarkdown;
    const markdownProps = showMarkdown
      ? {
          fullPrice,
          stickerLabel: { text: discountLabel, variant: 'error' },
        }
      : {};

    const stickerLabel = item?.variantBySkuId?.stickerLabel;

    return (
      <InlineErrorBoundary displayErrorMessaging={false}>
        <div data-testid="product-card">
          <ProductCard
            sku={item?.variantBySkuId?.id}
            href={shopItemPath({
              skuId: item?.variantBySkuId?.id,
              url: item?.variantBySkuId?.url || '',
            })}
            heroMedia={formatShopProductImage(item)}
            heroStageAspectRatio="1:1"
            brand={brand}
            heroSecondaryAction={
              showFeedbackThumbs ? (
                <FeedbackThumbs
                  skuId={item?.variantBySkuId?.id}
                  itemIndex={index}
                  source="PRODUCT_CAROUSEL"
                  page="product"
                  sourceId={null}
                />
              ) : null
            }
            stickerLabel={
              stickerLabel && {
                text: stickerLabel?.label,
                variant: stickerLabel?.theme?.toLowerCase() || undefined,
              }
            }
            name={name}
            price={price}
            onClick={onItemClick}
            {...markdownProps}
            cardSize="small"
          />
        </div>
      </InlineErrorBoundary>
    );
  };

  const onClickViewAllLink = () => {
    reportSelectEvent({
      schema: 'category_select',
      name: 'view_all_link',
      action_name: 'navigate_to_category',
      category_id: collectionTrackingId,
    });
    onClickViewAll();
  };

  const viewAllPath = () => {
    return {
      similar_items: null,
      best_brands: brandCategoryLink,
    }[treatment];
  };

  const renderedItems = items.map(item => {
    return (
      <InView
        threshold={1}
        styleName="item_container"
        key={item.id}
        onChange={inView => {
          onItemVisible(inView);
        }}
        triggerOnce
      >
        {renderItem(item)}
      </InView>
    );
  });

  return (
    <ErrorBoundary displayErrorMessaging={false}>
      <EventProvider>
        <div ref={ref}>
          <Box mb={1.5}>
            <ModuleHeader
              header={title}
              cta={
                viewAllPath() && {
                  onClick: onClickViewAllLink,
                  text: 'Shop more',
                  url: viewAllPath(),
                  'data-testid': 'shop-more-button',
                }
              }
              mb="default"
            />
          </Box>
          <InView
            threshold={1}
            triggerOnce
            onChange={inView => onCarouselVisible(inView)}
          >
            <Carousel
              ariaLabel={title}
              gutterOverflow={{ sm: 1, lg: 2, xl: 0 }}
              slideWidth="220px"
              controlsOffsetTop="7.25rem"
            >
              {renderedItems}
            </Carousel>
          </InView>
        </div>
      </EventProvider>
    </ErrorBoundary>
  );
};

CollectionCarousel.propTypes = {
  collectionTrackingId: PropTypes.string.isRequired,
  // describes how the skus in the carousel relate to the anchor sku
  // of the PDP.
  treatment: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onItemClick: PropTypes.func,
  onItemVisible: PropTypes.func,
  onCarouselVisible: PropTypes.func,
  onClickEndCard: PropTypes.func,
  onClickViewAll: PropTypes.func,
  onEndCardVisible: PropTypes.func,
  shopItemPath: PropTypes.func,
  brandCategoryLink: PropTypes.string,
};

CollectionCarousel.defaultProps = {
  onItemClick: () => {},
  onItemVisible: () => {},
  onCarouselVisible: () => {},
  onClickEndCard: () => {},
  onClickViewAll: () => {},
  onEndCardVisible: () => {},
  shopItemPath: () => {},
  brandCategoryLink: null,
};

export default CollectionCarousel;
